import {
    Alert,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { getAtomicSchemasForCollection } from '../../helpers/Api'
import { getBoostAction } from '../../helpers/WaxApi'
import { DropAlert } from '../DropAlert'
import { DropPaper } from '../DropPaper'
import { useSharedState } from '../../waxplorer/Waxplorer'
import MainButton from '../../common/util/input/MainButton'
import InputField from '../../common/util/InputField'

export const SchemaRequirements = ({
    collectionName,
    layers,
    staticAttributes,
    setSelectedSchema,
}) => {
    const [editSchemaName, setEditSchemaName] = useState(null)
    const [existingSchemaNames, setExistingSchemaNames] = useState([])
    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState(null)

    const [matchingSchemas, setMatchingSchemas] = useState(null)
    const [selectedEditSchema, setSelectedEditSchema] = useState(null)

    const [requiredSchemaAttributes, setRequiredSchemaAttributes] =
        useState(null)
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const createSchema = async () => {
        setIsCreating(true)
        const actions = [getBoostAction(activeUser)]

        let data = {
            authorized_creator: userName,
            collection_name: collectionName,
            schema_name: editSchemaName,
        }

        const attributes = { schema_format: requiredSchemaAttributes }
        data = { ...data, ...attributes }

        const action = {
            account: 'atomicassets',
            name: 'createschema',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: data,
        }
        actions.push(action)

        let createSchemaError = null
        let transactionId = null

        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            transactionId = result?.response?.transaction_id
        } catch (e) {
            createSchemaError = e.message
            setError(createSchemaError)
        }

        setIsCreating(false)

        if (error === null && transactionId !== null) {
            setSelectedSchema(editSchemaName)
        }
    }

    const parseSchemas = (schemas) => {
        const tempRequiredSchemaAttributes = []
        tempRequiredSchemaAttributes.push({ name: 'name', type: 'string' })
        tempRequiredSchemaAttributes.push({ name: 'img', type: 'image' })
        for (const attribute of staticAttributes) {
            tempRequiredSchemaAttributes.push({
                name: attribute.name,
                type: attribute.type,
            })
        }
        for (const layer of layers) {
            if (layer.attribute_name === 'None') {
                continue
            }
            tempRequiredSchemaAttributes.push({
                name: layer.attribute_name,
                type: 'string',
            })
        }

        const tempMatchingSchemas = []
        const tempExistingSchemaNames = []
        for (const schema of schemas) {
            tempExistingSchemaNames.push(schema.schema_name)
            const matchingSchemaAttributes = []
            for (const schemaAttribute of schema.format) {
                if (
                    !tempRequiredSchemaAttributes.find(
                        (requiredAttribute) =>
                            requiredAttribute.name === schemaAttribute.name &&
                            requiredAttribute.type === schemaAttribute.type,
                    )
                ) {
                    continue
                }
                matchingSchemaAttributes.push(schemaAttribute)
            }
            if (
                matchingSchemaAttributes.length ===
                tempRequiredSchemaAttributes.length
            ) {
                tempMatchingSchemas.push(schema)
            }
        }

        setExistingSchemaNames(tempExistingSchemaNames)
        setRequiredSchemaAttributes(tempRequiredSchemaAttributes)
        setMatchingSchemas(tempMatchingSchemas)
        if (tempMatchingSchemas.length > 0) {
            setSelectedEditSchema(tempMatchingSchemas[0].schema_name)
        }
    }

    const isEditSchemaNameInvalid = () => {
        if (editSchemaName === null || editSchemaName.length === 0) {
            return `schema name cannot be empty`
        } else if (existingSchemaNames.includes(editSchemaName)) {
            return `The schema ${editSchemaName} already exists in the ${collectionName} collection`
        } else {
            return null
        }
    }

    const fetchSchemas = () => {
        getAtomicSchemasForCollection(collectionName).then(parseSchemas)
    }

    useEffect(() => {
        if (collectionName) {
            fetchSchemas()
        }
    }, [collectionName])

    return (
        <>
            {matchingSchemas === null || isCreating ? (
                <div className="flex justify-center">
                    <CircularProgress />
                </div>
            ) : matchingSchemas.length === 0 && selectedEditSchema === null ? (
                <DropPaper error>
                    We couldn't find a schema in {collectionName} that matches
                    the layer structure you are trying to create. You will need
                    to create one.
                    <br />
                    It will need the following attributes:
                    <br />
                    <div className="flex flex-wrap justify-center mb-5">
                        {requiredSchemaAttributes
                            ? requiredSchemaAttributes.map((attribute) => (
                                  <div
                                      key={attribute.name + '_attribute'}
                                      className="border border-paperl rounded-md p-1 m-2"
                                  >
                                      {attribute.name} : {attribute.type}
                                  </div>
                              ))
                            : null}
                    </div>
                    <DropAlert
                        condition={error}
                        severity="error"
                        autoVerticalMargin={false}
                        className="mb-5"
                    >
                        {error}
                    </DropAlert>
                    <div className="flex flex-wrap justify-center items-center">
                        <div className="mx-3">
                            <InputField
                                variant="bordered"
                                label="schema name"
                                value={editSchemaName}
                                onChange={(e) =>
                                    setEditSchemaName(
                                        e.target.value
                                            .toLowerCase()
                                            .replaceAll(' ', ''),
                                    )
                                }
                                isInvalid={isEditSchemaNameInvalid() !== null}
                            />
                        </div>
                        <div className="mx-3">
                            <MainButton
                                className="mx-3 "
                                size="small"
                                isDisabled={isEditSchemaNameInvalid() !== null}
                                onClick={() => createSchema()}
                            >
                                {' '}
                                Create Schema
                            </MainButton>
                        </div>
                        <div className="mx-3">
                            <MainButton
                                className=" "
                                size="small"
                                onClick={() => fetchSchemas()}
                            >
                                {' '}
                                Refresh
                            </MainButton>
                        </div>
                    </div>
                    <DropAlert
                        condition={isEditSchemaNameInvalid() !== null}
                        severity={'error'}
                        className="mt-5"
                    >
                        {isEditSchemaNameInvalid()}
                    </DropAlert>
                </DropPaper>
            ) : (
                <DropPaper>
                    <Alert severity="info">
                        You will need a schema that reflects the layers you have
                        created. You can select a matching one from this
                        collection, or we can create one for you.
                    </Alert>
                    <DropAlert condition={error} severity="error">
                        {error}
                    </DropAlert>
                    <div className="my-5" />
                    <div className="flex flex-wrap justify-center">
                        <div>
                            <div className="flex flex-wrap justify-center">
                                <div className="w-54 mx-2 my-auto">
                                    <FormControl size="small" fullWidth={true}>
                                        <InputLabel>
                                            Select Existing Schema...
                                        </InputLabel>
                                        <Select
                                            value={
                                                selectedEditSchema
                                                    ? selectedEditSchema
                                                    : ''
                                            }
                                            label="Select Existing Schema..."
                                            onChange={(e) => {
                                                setSelectedEditSchema(
                                                    e.target.value,
                                                )
                                            }}
                                        >
                                            {matchingSchemas.map((schema) => (
                                                <MenuItem
                                                    value={schema.schema_name}
                                                >
                                                    {schema.schema_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                                <div className="w-54 mx-2 my-auto">
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        disabled={selectedEditSchema === null}
                                        onClick={() =>
                                            setSelectedSchema(
                                                selectedEditSchema,
                                            )
                                        }
                                    >
                                        Continue with selection
                                    </Button>
                                </div>
                            </div>
                            <div className="font-bold text-center my-5">
                                - OR -
                            </div>
                            <div className="flex flex-wrap justify-center">
                                <div className="w-54 mx-2 my-auto">
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="new schema name"
                                        value={editSchemaName}
                                        onChange={(e) =>
                                            setEditSchemaName(
                                                e.target.value
                                                    .toLocaleLowerCase()
                                                    .replaceAll(' ', ''),
                                            )
                                        }
                                        error={
                                            isEditSchemaNameInvalid() !== null
                                        }
                                    />
                                </div>
                                <div className="w-54 mx-2 my-auto">
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        disabled={
                                            isEditSchemaNameInvalid() !== null
                                        }
                                        onClick={(e) => createSchema()}
                                    >
                                        Create Schema
                                    </Button>
                                </div>
                            </div>
                            <DropAlert
                                condition={isEditSchemaNameInvalid() !== null}
                                severity={'error'}
                                className="mt-5"
                            >
                                {isEditSchemaNameInvalid()}
                            </DropAlert>
                        </div>
                    </div>
                </DropPaper>
            )}
        </>
    )
}
